import React from 'react';
import { Box, Paper, Typography, LinearProgress } from '@mui/material';
import BuildIcon from '@mui/icons-material/Build';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { useTranslation } from 'react-i18next';
import { theme } from './theme';

function JobsProgressCard({ jobs }) {
  const { t } = useTranslation();

  if (!jobs || jobs.length === 0) return null;

  const completedJobs = jobs.filter(j => j.is_completed).length;
  const totalJobs = jobs.length;
  const progressPercent = Math.round((completedJobs / totalJobs) * 100);

  return (
    <Paper
      elevation={0}
      sx={{
        backgroundColor: theme.colors.surface,
        borderRadius: theme.radius.xl,
        padding: '20px',
        boxShadow: theme.shadows.md,
        marginBottom: '20px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '16px',
        }}
      >
        <Typography
          component="h3"
          sx={{
            fontSize: '16px',
            fontWeight: '700',
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <BuildIcon sx={{ color: theme.colors.accent, fontSize: 20 }} />
          {t('public_maintenance.jobs')}
        </Typography>
        <Box
          sx={{
            padding: '4px 12px',
            backgroundColor: progressPercent === 100 ? theme.colors.successBg : theme.colors.borderLight,
            color: progressPercent === 100 ? theme.colors.success : theme.colors.textMuted,
            borderRadius: theme.radius.full,
            fontSize: '13px',
            fontWeight: '600',
          }}
        >
          {completedJobs}/{totalJobs} {t('public_maintenance.jobs_completed')}
        </Box>
      </Box>

      {/* Progress bar */}
      <Box sx={{ marginBottom: '16px' }}>
        <LinearProgress
          variant="determinate"
          value={progressPercent}
          sx={{
            height: '8px',
            borderRadius: theme.radius.full,
            backgroundColor: theme.colors.borderLight,
            '& .MuiLinearProgress-bar': {
              backgroundColor: progressPercent === 100 ? theme.colors.success : theme.colors.accent,
              borderRadius: theme.radius.full,
            },
          }}
        />
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {jobs.map((job) => (
          <Box
            key={job.id}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '12px',
              backgroundColor: job.is_completed ? theme.colors.successBg : theme.colors.background,
              borderRadius: theme.radius.md,
            }}
          >
            {job.is_completed ? (
              <CheckCircleIcon sx={{ color: theme.colors.success, fontSize: 24 }} />
            ) : (
              <RadioButtonUncheckedIcon sx={{ color: theme.colors.border, fontSize: 24 }} />
            )}
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: '14px',
                  color: job.is_completed ? theme.colors.success : theme.colors.text,
                  textDecoration: job.is_completed ? 'line-through' : 'none',
                  opacity: job.is_completed ? 0.8 : 1,
                }}
              >
                {job.name}
              </Typography>
              {job.description && (
                <Typography
                  sx={{
                    fontSize: '12px',
                    color: theme.colors.textMuted,
                    whiteSpace: 'pre-line',
                    mt: '2px',
                  }}
                >
                  {job.description}
                </Typography>
              )}
            </Box>
          </Box>
        ))}
      </Box>
    </Paper>
  );
}

export default JobsProgressCard;
